/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Context } from '@datadog/browser-core';
import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { overlay } from 'overlay-kit';
import { type Store } from 'redux';
import { v1 as uuidv1 } from 'uuid';

import Alert from '@/components/shared/Alert';
import { spenditHistory } from '@/new_src/view/bootstrap/config/configureHistory';
import { Text } from '@/new_src/view/components/Text';
import { translate, translateHTML } from '@/utils/LocaleUtil';
import { fireError } from '@/utils/SwalUtil';

import { axiosActions, type MaintenanceErrorResponse } from 'modules/axios';
import DatadogLogUtil from 'utils/DatadogLogUtil';
import { isAWSSlowDownErrorResponse, isSpenditErrorResponseData } from 'utils/ErrorHandler';

import { type SpenditApiVersions } from './shared';
import { isCertificateSchemaUnavailableResponse } from './UserCardAPI/certificate';

type InvalidSessionErrorResponse = { code: 502; message: string };

export const isLegacyInvalidSessionErrorResponse = (
  response: AxiosResponse
): response is AxiosResponse<InvalidSessionErrorResponse> => response.data?.code === 502;

export const isMaintenanceErrorResponse = (
  payload?: AxiosResponse
): payload is AxiosResponse<MaintenanceErrorResponse> => {
  /**
   * AWS의 S3 url을 이용해 요청하는 경우(putEditorContent) 503 에러가 발생할 수 있음. 이 경우 유지보수중 에러로 처리하지 않음.
   */
  if (isAWSSlowDownErrorResponse(payload) || isCertificateSchemaUnavailableResponse(payload)) {
    return false;
  }
  return payload?.status === 503;
};

export type ResponseLogContextType = {
  statusCode: number;
  statusText: string;
  responseURL: string;
  requestId: string;
  responseTimestamp: number;
  duration: number;
  responseData?: Context;
} & Pick<AxiosRequestConfig, 'method' | 'params' | 'url'>;

export const isAxiosResponse = (response: AxiosResponse | AxiosError): response is AxiosResponse => 'data' in response;

export const extractErrorCodeFromAxiosError = (error: AxiosError): number | undefined => {
  return error.response?.data?.error?.code;
};

/**
 * 로깅을 위한 컨텍스트 정보 생성
 */
const buildResponseLogContext = (response: AxiosResponse | AxiosError): ResponseLogContextType => {
  const requestId = response.config.headers['Spendit-Request-ID'];
  const requestTimestamp = Number(response.config.headers['Spendit-Request-Timestamp']);
  const { method, params, url } = response.config;
  const { status: statusCode, statusText, responseURL } = response.request;
  const responseTimestamp = Date.now();

  return {
    statusCode,
    statusText,
    responseURL,
    requestId,
    responseTimestamp,
    duration: (responseTimestamp - requestTimestamp) * 1_000_000,
    method,
    params,
    responseData: isAxiosResponse(response) ? response?.data : response.response?.data,
    url,
  };
};

// For HTTP 2.0 Multiplexing
const MAX_REQUESTS_COUNT = 5;
const INTERVAL_MS = 10;

export const configureAxiosInstance = (store: Store, axiosInstance: AxiosInstance) => {
  let pendingRequests = 0;
  let offlineNetworkErrorOverlayId: string | null = null;

  axiosInstance.interceptors.request.use(
    axiosRequestConfig => {
      return new Promise(resolve => {
        const { method, params, url, timeout, data } = axiosRequestConfig;

        const interval = setInterval(() => {
          if (pendingRequests < MAX_REQUESTS_COUNT) {
            pendingRequests += 1;
            clearInterval(interval);

            const requestId = uuidv1();
            const requestTimestamp = Date.now();

            DatadogLogUtil.info(`axios.request.onFulfilled - ${method} ${url}`, {
              requestId,
              requestTimestamp,
              method,
              params,
              data,
              url,
            });

            axiosRequestConfig.headers['Spendit-Request-ID'] = requestId;
            axiosRequestConfig.headers['Spendit-Request-Timestamp'] = requestTimestamp;
            axiosRequestConfig.timeout = timeout ?? Number(process.env.REACT_APP_DEFAULT_API_TIMEOUT);

            resolve(axiosRequestConfig);
          }
        }, INTERVAL_MS);
      });
    },
    err => {
      DatadogLogUtil.debug(`axios.request.onRejected`, err);

      return Promise.reject(err);
    }
  );

  axiosInstance.interceptors.response.use(
    response => {
      pendingRequests = Math.max(0, pendingRequests - 1);
      const context = buildResponseLogContext(response);
      const msg = `axios.response.onFulfilled - ${context.method} ${context.url} ${context.statusCode} ${context.statusText}`;
      DatadogLogUtil.info(msg, context);
      return response;
    },
    error => {
      pendingRequests = Math.max(0, pendingRequests - 1);
      if (axios.isAxiosError(error)) {
        const context = buildResponseLogContext(error);
        let msg = `axios.response.onRejected - ${context.method} ${context.url} ${context.statusCode} ${context.statusText}`;

        if (isMaintenanceErrorResponse(error.response)) {
          store.dispatch(axiosActions.globalErrorOccurred({ type: 'service_maintenance', data: error.response.data }));
        } else if (error.response) {
          const data = error.response.data;
          Object.assign(context, data);
          if (isSpenditErrorResponseData(data)) {
            if (data.error.code === 40490) {
              spenditHistory.push('/');
            }
          }
        } else if (error.request) {
          const { code, message } = error.toJSON() as { code?: string; message: string };

          msg += ` [${code}] ${message}`;
          const isOffline = !navigator.onLine;

          if (code === 'ECONNABORTED') {
            // timeout 오류
            fireError('서버로부터 응답이 없습니다.');
          } else if (isOffline) {
            if (!offlineNetworkErrorOverlayId) {
              const overlayId = overlay.open(({ isOpen, close }) => {
                const handleClose = () => {
                  offlineNetworkErrorOverlayId = null;
                  close();
                };

                return (
                  <Alert
                    status="emergency"
                    title={translate('network_error_title')}
                    html={
                      <Text c="gray.8" size="14px" lh="120%">
                        {translateHTML('network_error_description')}
                      </Text>
                    }
                    confirmButtonLabel={translate('ok')}
                    open={isOpen}
                    showCancelButton={false}
                    onConfirm={handleClose}
                    onClose={handleClose}
                  />
                );
              });
              offlineNetworkErrorOverlayId = overlayId;
            }
          } else {
            // 그 외 (네트워크 오류)
            // fireError(translate('alert_network_disconnected'));
          }
        } else {
          msg += ` ${error.message}`;
          fireError('오류가 발생했습니다. 관리자에게 문의해주세요.');
        }

        DatadogLogUtil.debug(msg, context);
      }

      return Promise.reject(error);
    }
  );
};

export const getUrlWithApiVersion = (version: SpenditApiVersions, ...path: string[]) => {
  const paths = [version, ...path];
  return `/${paths.join('/')}`;
};

const appendFormData = (formData: FormData, data: any, parentKey?: string) => {
  if (data === undefined || data === null) {
    return;
  }

  // File / Blob은 그대로 추가
  if (data instanceof File || data instanceof Blob) {
    formData.append(parentKey, data);
    return;
  }

  // 배열 처리
  if (Array.isArray(data)) {
    // 객체 배열인지(=모든 원소가 "일반 객체") 판별
    const isObjectArray = data.length > 0 && data.every(item => typeof item === 'object' && item !== null);

    if (isObjectArray) {
      // 객체 배열 -> 인덱스 키 사용: parent[0][field]
      data.forEach((value, index) => {
        const key = parentKey ? `${parentKey}[${index}]` : String(index);
        appendFormData(formData, value, key);
      });
    } else {
      // 값 배열(원시/파일/Blob 포함 가능) -> [] 키 사용: parent[]
      data.forEach(value => {
        const key = parentKey ? `${parentKey}[]` : '[]';
        appendFormData(formData, value, key);
      });
    }
    return;
  }
  // 객체 처리
  if (typeof data === 'object' && data !== null) {
    Object.keys(data).forEach(key => {
      const value = data[key];
      const fullKey = parentKey ? `${parentKey}[${key}]` : key;
      appendFormData(formData, value, fullKey);
    });
    return;
  }

  if (!parentKey) {
    throw new Error('appendFormData: parentKey is required for primitive values');
  }

  // 기본값(문자열, 숫자, 불린)
  formData.append(parentKey, data);
};

export const convertToFormData = (data: any) => {
  const formData = new FormData();
  appendFormData(formData, data);
  return formData;
};

type DataError = {
  error: {
    code: number;
    message: string;
    context: unknown; // TODO: {}로 오는 것만 확인
  };
};

// TODO: axios.isAxiosError 사용처들 래핑
export function hasErrorResponse<T = DataError>(
  error: unknown
): error is AxiosError<T> & { response: AxiosResponse<T> } {
  return !!error && axios.isAxiosError(error) && error.response !== undefined;
}

const isErrorWithStatus = (error: unknown, status: number) => {
  return hasErrorResponse(error) && error.response.status === status;
};

export const isForbiddenError = (error: unknown) => {
  return isErrorWithStatus(error, 403);
};

export const isNotFoundError = (error: unknown) => {
  return isErrorWithStatus(error, 404);
};
